// RemarksModal.tsx
import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Typography,
    IconButton,
    Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import MuiButton from "@components/Button/MuiButton";
import { AppDispatch } from "@store/store";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import {
    getFileROWSignedURLThunk,
    getFileSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { openFileBySignedUrl } from "../../utils/getViewSignedUrlHandler";

const MAX_REMARKS = 2000;

export interface RemarksAttachment {
    fileName: string;         // S3 fileName (used for view)
    originalFileName: string; // original uploaded name
    objectKey: string;
    mimeType: string;
}

interface RemarksModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * Called with remarks + uploaded attachments.
     * Kept backward-compatible: if you pass only remarks, it still works.
     */
    onSubmit: (remarks: string, attachments: RemarksAttachment[]) => void;
    loading?: boolean;
    /** Access id (used for module type + signed url thunk selection) */
    accessId?: string | null;
}

export const RemarksModal: React.FC<RemarksModalProps> = ({
    open,
    onClose,
    onSubmit,
    loading = false,
    accessId,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const [remarks, setRemarks] = useState("");
    const [error, setError] = useState("");
    const [attachments, setAttachments] = useState<RemarksAttachment[]>([]);

    // ---------- File upload ----------
    const handleAttachmentUpload = async (
        file: File,
        fileInput: HTMLInputElement,
    ) => {
        try {
            if (file.size > 21 * 1024 * 1024) {
                toast.error("File size must be less than 20 MB");
                return;
            }

            const fileUploadURL = await dispatch(
                genericUploadThunk({
                    file,
                    moduleType: accessId ? "access" : "row",
                }),
            );

            if (!fileUploadURL?.payload?.success) {
                const details =
                    fileUploadURL?.payload?.response?.data?.details ||
                    fileUploadURL?.payload?.details;

                toast.error(
                    Array.isArray(details)
                        ? details[0]
                        : typeof details === "string"
                            ? details
                            : "Something Went Wrong!",
                );
                return;
            }

            if (!productionCheck) {
                const uploadSuccess = await dispatch(
                    uploadToS3Thunk({
                        signedUrl: fileUploadURL.payload.data.signedUrl,
                        file,
                        contentType: file.type,
                    }),
                ).unwrap();

                if (!uploadSuccess) throw new Error("S3 upload failed");
            }

            const uploaded: RemarksAttachment = {
                fileName: fileUploadURL.payload.data.fileName,
                originalFileName: file.name,
                objectKey: fileUploadURL.payload.data.objectKey,
                mimeType: file.type || "application/pdf",
            };

            setAttachments((prev) => [...prev, uploaded]);
            toast.success("File uploaded successfully");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Failed to upload file");
        } finally {
            fileInput.value = "";
        }
    };

    const handleAttachmentChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const fileInput = event.target;
        const file = fileInput.files?.[0];
        if (!file) return;
        await handleAttachmentUpload(file, fileInput);
    };

    const handleViewFile = (fileName: string) => {
        openFileBySignedUrl({
            document_name: fileName,
            dispatch,
            getSignedUrlThunk: accessId
                ? getFileSignedURLThunk
                : getFileROWSignedURLThunk,
        });
    };

    const handleDeleteAttachment = (index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    // ---------- Submit ----------
    const handleSubmit = () => {
        if (!remarks.trim()) {
            setError("Remarks is required");
            return;
        }
        onSubmit(remarks, attachments);
        // reset after submit
        setRemarks("");
        setAttachments([]);
        setError("");
    };

    const handleClose = () => {
        setRemarks("");
        setAttachments([]);
        setError("");
        onClose();
    };

    const getTruncatedFileName = (fileName: string, maxLength = 25) => {
        const lastDot = fileName?.lastIndexOf(".");
        if (lastDot === -1) return fileName;
        const name = fileName?.slice(0, lastDot);
        const ext = fileName?.slice(lastDot);
        return (
            (name?.length > maxLength ? name?.slice(0, maxLength) + "..." : name) +
            ext
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{
                    fontWeight: 600,
                    color: "#3A1C73",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                Add Remarks
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: "#666" }}>
                        Please provide remarks before submitting
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter your remarks here..."
                        value={remarks}
                        onChange={(e) => {
                            setRemarks(e.target.value.slice(0, MAX_REMARKS));
                            if (error) setError("");
                        }}
                        error={!!error}
                        helperText={error}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: "12px",
                            color: "#9CA3AF",
                            textAlign: "right",
                            mt: 0.5,
                        }}
                    >
                        {remarks.length}/{MAX_REMARKS}
                    </Typography>
                </Box>

                {/* Upload Documents */}
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Upload Documents (optional)
                    </Typography>

                    <Button
                        component="label"
                        variant="outlined"
                        sx={{ mb: 2, textTransform: "none" }}
                    >
                        Upload Document
                        <input
                            hidden
                            type="file"
                            accept=".pdf"
                            onChange={handleAttachmentChange}
                        />
                    </Button>

                    {attachments.length > 0 && (
                        <Box sx={{ maxWidth: "100%" }}>
                            {attachments.map((att, index) => (
                                <Box
                                    key={`${att.objectKey}-${index}`}
                                    sx={{
                                        border: "1px solid #9FA3A9",
                                        borderRadius: "8px",
                                        px: 1.8,
                                        py: 1.15,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                        backgroundColor: "#F6F6F7",
                                    }}
                                >
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <PictureAsPdfIcon sx={{ color: "#D2413A" }} />
                                        <Typography>
                                            {getTruncatedFileName(att.originalFileName)}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <IconButton
                                            onClick={() => handleViewFile(att.fileName)}
                                        >
                                            <CloudDownloadOutlinedIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteAttachment(index)}
                                        >
                                            <DeleteOutlineOutlinedIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 0 }}>
                <MuiButton
                    type="button"
                    name="Cancel"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                    sx={{
                        border: "1px solid #3A1C73",
                        color: "#3A1C73",
                        borderRadius: "50px",
                        width: "fit-content",
                        "&:hover": { background: "#3A1C73", color: "#fff" },
                    }}
                />
                <MuiButton
                    type="button"
                    name="Submit"
                    variant="outlined"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        border: "1px solid #3A1C73",
                        color: "#fff",
                        background: "#3A1C73",
                        borderRadius: "50px",
                        width: "fit-content",
                        "&:hover": { background: "#2A1253" },
                    }}
                />
            </DialogActions>
        </Dialog>
    );
};